import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTransactions } from '@/hooks/useTransactions';
import { Loader2, CreditCard } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '@/integrations/supabase/client';

// Stripe publishable key is public by design, but sourcing it from env
// lets production/test environments use different keys without a code
// change. Set VITE_STRIPE_PUBLISHABLE_KEY in .env (dev) and in the
// hosting platform's env vars (prod).
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
if (!STRIPE_PUBLISHABLE_KEY) {
  // eslint-disable-next-line no-console
  console.error('VITE_STRIPE_PUBLISHABLE_KEY is not set — Stripe Elements will not initialize.');
}
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : Promise.resolve(null);

interface DepositModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PaymentForm = ({ amount, onSuccess, paymentIntentId }: { amount: number; onSuccess: () => void; paymentIntentId: string }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/wallet`,
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message || 'Payment failed');
      } else {
        // Confirm the deposit to credit the wallet immediately
        await supabase.functions.invoke('confirm-deposit', {
          body: { paymentIntentId },
        });
        onSuccess();
      }
    } catch (err) {
      setError('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" disabled={!stripe || loading} className="w-full">
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Pay ${amount.toFixed(2)}
      </Button>
    </form>
  );
};

export const DepositModal = ({ open, onOpenChange }: DepositModalProps) => {
  const [amount, setAmount] = useState('');
  const [step, setStep] = useState<'amount' | 'payment'>('amount');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { createDeposit } = useTransactions();

  useEffect(() => {
    if (!open) {
      setStep('amount');
      setAmount('');
      setClientSecret(null);
      setPaymentIntentId(null);
    }
  }, [open]);

  const handleContinue = async () => {
    const numAmount = parseFloat(amount);

    if (isNaN(numAmount) || numAmount < 10) {
      return;
    }

    setLoading(true);
    try {
      const result = await createDeposit(numAmount);
      if (result?.client_secret) {
        setClientSecret(result.client_secret);
        setPaymentIntentId(result.payment_intent_id);
        setStep('payment');
      }
    } catch (error) {
      console.error('Deposit failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    onOpenChange(false);
    setAmount('');
    setStep('amount');
    setClientSecret(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Deposit Funds
            </div>
          </DialogTitle>
          <DialogDescription>
            {step === 'amount' 
              ? 'Add funds to your wallet. Minimum deposit is $10.' 
              : 'Enter your payment details to complete the deposit.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'amount' && (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (USD)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="amount"
                    type="number"
                    min="10"
                    max="10000"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Min: $10 | Max: $10,000 per transaction
                </p>
              </div>

              <div className="p-4 rounded-lg bg-muted">
                <div className="flex justify-between text-sm">
                  <span>Amount:</span>
                  <span className="font-medium">${parseFloat(amount || '0').toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span>Fee:</span>
                  <span className="font-medium text-green-600">Free</span>
                </div>
                <div className="flex justify-between text-base font-semibold mt-3 pt-3 border-t">
                  <span>Total:</span>
                  <span>${parseFloat(amount || '0').toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleContinue}
                className="flex-1"
                disabled={loading || !amount || parseFloat(amount) < 10}
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Continue
              </Button>
            </div>
          </>
        )}

        {step === 'payment' && clientSecret && paymentIntentId && (
          <div className="py-4">
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentForm
                amount={parseFloat(amount)}
                onSuccess={handleSuccess}
                paymentIntentId={paymentIntentId}
              />
            </Elements>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};